'use client'

import { memo } from 'react'
import { useNavigate } from '../../lib/next-router-compat'
import { ListView } from '@heroui-pro/react'
import { BriefcaseBusiness } from 'lucide-react'
import type { Deal } from '../../lib/types'
import { daysUntil, dealStatusTone, formatDate, formatMoney, isOpenDealStatus, titleCase } from '../../lib/view'
import { StatusChip } from '../../components/StatusChip'

export function getClosingSoonCount(deals: Deal[]): number {
  return deals.filter((deal) => {
    const days = daysUntil(deal.close_of_escrow_date)
    return isOpenDealStatus(deal.status) && days !== null && days >= 0 && days <= 5
  }).length
}

export function sortByUpcomingClose(deals: Deal[]): Deal[] {
  return [...deals]
    .filter((deal) => deal.close_of_escrow_date)
    .sort((a, b) => (daysUntil(a.close_of_escrow_date) ?? 999) - (daysUntil(b.close_of_escrow_date) ?? 999))
}

function RecentDealRow({ deal }: { deal: Deal }): React.ReactElement {
  const closeDays = daysUntil(deal.close_of_escrow_date)

  return (
    <ListView.Item id={deal.id} textValue={deal.property_address}>
      <ListView.ItemContent>
        <BriefcaseBusiness className="size-4" />
        <div className="min-w-0">
          <ListView.Title>{deal.property_address}</ListView.Title>
          <ListView.Description>
            {deal.property_city}, {deal.property_state} · {titleCase(deal.financing_type)} · {formatMoney(deal.purchase_price)}
          </ListView.Description>
        </div>
      </ListView.ItemContent>
      <ListView.ItemAction>
        <div className="flex min-w-0 items-center gap-3">
          <StatusChip tone={dealStatusTone(deal.status)} status={deal.status} />
          <div className="hidden text-right sm:block">
            <p className="text-sm font-semibold tabular-nums text-foreground">{formatDate(deal.close_of_escrow_date)}</p>
            <p className="text-xs text-muted">{closeDays === null ? 'Close' : `${closeDays} days`}</p>
          </div>
        </div>
      </ListView.ItemAction>
    </ListView.Item>
  )
}

export const PriorityDealList = memo(function PriorityDealList({ deals }: { deals: Deal[] }): React.ReactElement {
  const navigate = useNavigate()

  return (
    <ListView
      aria-label="Priority deals"
      items={deals}
      selectionMode="none"
      variant="secondary"
      onAction={(key) => { void navigate(`/deals/${String(key)}`) }}
    >
      {(deal) => (
        <RecentDealRow deal={deal} />
      )}
    </ListView>
  )
})
